import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { usePermission } from '@/components/guards/permission-guard';
import { formatCurrency } from '@/lib/utils';
import {
  Ticket,
  Plus,
  Search,
  Copy,
  Check,
  MoreVertical,
  Pencil,
  Trash2,
  ToggleLeft,
  ToggleRight,
  Loader2,
  Percent,
  DollarSign,
  Calendar,
  Users,
  BarChart3,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { trpc } from '@/lib/trpc';
import { PromoCodeModal } from '@/components/travel/marketing/promo-code-modal';

export default function PromoCodesPage() {
  const { t } = useTranslation('promoCodes');
  const canCreate = usePermission('PROMO_CODES', 'CREATE');
  const canEdit = usePermission('PROMO_CODES', 'EDIT');
  const canDelete = usePermission('PROMO_CODES', 'DELETE');
  const [search, setSearch] = useState('');
  const [showExpired, setShowExpired] = useState(false);
  const [page, setPage] = useState(1);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingCode, setEditingCode] = useState<string | null>(null);
  const [copiedCode, setCopiedCode] = useState<string | null>(null);

  const { data, isLoading, refetch } = trpc.promoCode.list.useQuery({
    page,
    search: search || undefined,
    includeExpired: showExpired,
  });

  const { data: stats } = trpc.promoCode.stats.useQuery();

  const toggleActiveMutation = trpc.promoCode.toggleActive.useMutation({
    onSuccess: () => refetch(),
  });

  const deleteMutation = trpc.promoCode.delete.useMutation({
    onSuccess: () => refetch(),
  });

  const handleCopyCode = (code: string) => {
    navigator.clipboard.writeText(code);
    setCopiedCode(code);
    setTimeout(() => setCopiedCode(null), 2000);
  };

  const handleEdit = (id: string) => {
    setEditingCode(id);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setEditingCode(null);
    refetch();
  };

  const formatDiscount = (type: string, value: number) => {
    if (type === 'PERCENTAGE') {
      return `${value}%`;
    }
    return `$${value.toFixed(2)}`;
  };

  const getStatusBadge = (code: {
    isActive: boolean;
    validFrom: Date;
    validTo: Date;
  }) => {
    const now = new Date();
    const validTo = new Date(code.validTo);

    if (!code.isActive) {
      return (
        <span className="inline-flex items-center bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-600">
          {t('inactive')}
        </span>
      );
    }

    if (validTo < now) {
      return (
        <span className="inline-flex items-center bg-red-100 px-2 py-0.5 text-xs font-medium text-red-600">
          {t('expired')}
        </span>
      );
    }

    return (
      <span className="inline-flex items-center bg-green-100 px-2 py-0.5 text-xs font-medium text-green-600">
        {t('active')}
      </span>
    );
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">{t('title')}</h1>
          <p className="text-muted-foreground">{t('subtitle')}</p>
        </div>
        {canCreate && (
          <Button onClick={() => setIsModalOpen(true)}>
            <Plus className="mr-2 h-4 w-4" />
            {t('createNew')}
          </Button>
        )}
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid gap-4 md:grid-cols-5">
          <div className="border border-border bg-card p-4">
            <div className="flex items-center gap-3">
              <div className="bg-blue-100 p-2">
                <Ticket className="h-5 w-5 text-blue-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">{t('stats.totalCodes')}</p>
                <p className="text-2xl font-bold">{stats.total}</p>
              </div>
            </div>
          </div>
          <div className="border border-border bg-card p-4">
            <div className="flex items-center gap-3">
              <div className="bg-green-100 p-2">
                <ToggleRight className="h-5 w-5 text-green-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">{t('stats.activeCodes')}</p>
                <p className="text-2xl font-bold">{stats.active}</p>
              </div>
            </div>
          </div>
          <div className="border border-border bg-card p-4">
            <div className="flex items-center gap-3">
              <div className="bg-red-100 p-2">
                <Calendar className="h-5 w-5 text-red-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">{t('stats.expiredCodes')}</p>
                <p className="text-2xl font-bold">{stats.expired}</p>
              </div>
            </div>
          </div>
          <div className="border border-border bg-card p-4">
            <div className="flex items-center gap-3">
              <div className="bg-purple-100 p-2">
                <Users className="h-5 w-5 text-purple-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">{t('stats.totalUsages')}</p>
                <p className="text-2xl font-bold">{stats.totalUsages}</p>
              </div>
            </div>
          </div>
          <div className="border border-border bg-card p-4">
            <div className="flex items-center gap-3">
              <div className="bg-amber-100 p-2">
                <BarChart3 className="h-5 w-5 text-amber-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">{t('stats.totalDiscount')}</p>
                <p className="text-2xl font-bold">{formatCurrency(stats.totalDiscountGiven, 'TRY')}</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="flex items-center gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <input
            type="text"
            placeholder={t('searchPlaceholder')}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="h-8 w-full border border-border bg-background pl-10 pr-4 text-sm focus:border-foreground focus:outline-none focus:ring-1 focus:ring-foreground/20"
          />
        </div>
        <label className="flex items-center gap-2 text-sm text-muted-foreground">
          <input
            type="checkbox"
            checked={showExpired}
            onChange={(e) => setShowExpired(e.target.checked)}
            className="border border-border"
          />
          {t('showExpired')}
        </label>
      </div>

      {/* Table */}
      <div className="border border-border bg-card overflow-hidden">
        {isLoading ? (
          <div className="flex items-center justify-center p-12">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
          </div>
        ) : data?.promoCodes.length === 0 ? (
          <div className="p-12 text-center">
            <Ticket className="mx-auto h-12 w-12 text-muted-foreground/50" />
            <h3 className="mt-4 text-lg font-medium">{t('noCodesFound')}</h3>
            <p className="mt-2 text-muted-foreground">{t('createFirstCode')}</p>
            {canCreate && (
              <Button className="mt-4" onClick={() => setIsModalOpen(true)}>
                <Plus className="mr-2 h-4 w-4" />
                {t('createNew')}
              </Button>
            )}
          </div>
        ) : (
          <table className="w-full">
            <thead className="bg-muted/50 border-b">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                  {t('code')}
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                  {t('discount')}
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                  {t('validPeriod')}
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                  {t('usage')}
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                  {t('status')}
                </th>
                <th className="px-4 py-3 text-right text-xs font-medium text-muted-foreground uppercase tracking-wider">
                  {t('actions')}
                </th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {data?.promoCodes.map((code) => (
                <tr key={code.id} className="hover:bg-muted/30">
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <span className="font-mono font-semibold">{code.code}</span>
                      <button
                        onClick={() => handleCopyCode(code.code)}
                        className="text-muted-foreground hover:text-foreground"
                        title={t('copyCode')}
                      >
                        {copiedCode === code.code ? (
                          <Check className="h-4 w-4 text-green-500" />
                        ) : (
                          <Copy className="h-4 w-4" />
                        )}
                      </button>
                    </div>
                    {code.description && (
                      <p className="text-sm text-muted-foreground mt-0.5">{code.description}</p>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      {code.discountType === 'PERCENTAGE' ? (
                        <Percent className="h-4 w-4 text-muted-foreground" />
                      ) : (
                        <DollarSign className="h-4 w-4 text-muted-foreground" />
                      )}
                      <span className="font-medium">
                        {formatDiscount(code.discountType, code.discountValue)}
                      </span>
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <div className="text-sm">
                      <p>{new Date(code.validFrom).toLocaleDateString()}</p>
                      <p className="text-muted-foreground">
                        {t('dateTo')}{new Date(code.validTo).toLocaleDateString()}
                      </p>
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <span className="text-sm">
                      {code.usageCount} {t('of')}{' '}
                      {code.maxUses ?? t('unlimited')}
                    </span>
                  </td>
                  <td className="px-4 py-3">{getStatusBadge(code)}</td>
                  <td className="px-4 py-3 text-right">
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="sm">
                          <MoreVertical className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        {canEdit && (
                          <DropdownMenuItem onClick={() => handleEdit(code.id)}>
                            <Pencil className="mr-2 h-4 w-4" />
                            {t('edit')}
                          </DropdownMenuItem>
                        )}
                        {canEdit && (
                          <DropdownMenuItem
                            onClick={() => toggleActiveMutation.mutate(code.id)}
                          >
                            {code.isActive ? (
                              <>
                                <ToggleLeft className="mr-2 h-4 w-4" />
                                {t('deactivate')}
                              </>
                            ) : (
                              <>
                                <ToggleRight className="mr-2 h-4 w-4" />
                                {t('activate')}
                              </>
                            )}
                          </DropdownMenuItem>
                        )}
                        {canDelete && (
                          <>
                            {code.usageCount === 0 && (
                              <DropdownMenuItem
                                onClick={() => deleteMutation.mutate(code.id)}
                                className="text-red-600"
                              >
                                <Trash2 className="mr-2 h-4 w-4" />
                                {t('delete')}
                              </DropdownMenuItem>
                            )}
                          </>
                        )}
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}

        {/* Pagination */}
        {data && data.pagination.totalPages > 1 && (
          <div className="flex items-center justify-between border-t border-border px-4 py-3">
            <p className="text-sm text-muted-foreground">
              {t('pagination.pageOf', { current: data.pagination.page, total: data.pagination.totalPages })}
            </p>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                className="h-8"
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page === 1}
              >
                {t('pagination.previous')}
              </Button>
              <Button
                variant="outline"
                size="sm"
                className="h-8"
                onClick={() => setPage((p) => p + 1)}
                disabled={page === data.pagination.totalPages}
              >
                {t('pagination.next')}
              </Button>
            </div>
          </div>
        )}
      </div>

      {/* Modal */}
      <PromoCodeModal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        editingId={editingCode}
      />
    </div>
  );
}
